"use client";

import { useState, useCallback } from "react";

interface UsePasswordValidationOptions {
  t: (key: string) => string;
  mode:
    | "select"
    | "email-login"
    | "email-signup"
    | "email-sent"
    | "forgot-password"
    | "forgot-password-sent";
}

interface UsePasswordValidationReturn {
  password: string;
  passwordError: string | null;
  hasSubmittedOnce: boolean;
  handlePasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  validateAndSetError: () => string | null;
  markAsSubmitted: () => void;
  resetPasswordState: () => void;
}

/**
 * Custom hook for password validation with submit-then-realtime pattern.
 *
 * Validation rules:
 * - Minimum 8 characters
 * - At least one lowercase letter
 * - At least one number
 * - At least one special character
 *
 * Validation timing:
 * - Before first submit: No error shown
 * - On submit: Show error if invalid
 * - After first submit: Real-time validation on every change
 */
export function usePasswordValidation({
  t,
  mode,
}: UsePasswordValidationOptions): UsePasswordValidationReturn {
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [hasSubmittedOnce, setHasSubmittedOnce] = useState(false);

  const validatePassword = useCallback(
    (pwd: string): string | null => {
      if (pwd.length < 8) return t("passwordTooShort");
      const hasLowercase = /[a-z]/.test(pwd);
      const hasNumber = /[0-9]/.test(pwd);
      const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd);
      if (!hasLowercase || !hasNumber || !hasSpecial)
        return t("passwordRequirements");
      return null;
    },
    [t]
  );

  const handlePasswordChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newPassword = e.target.value;
      setPassword(newPassword);
      if (hasSubmittedOnce && mode === "email-signup") {
        setPasswordError(validatePassword(newPassword));
      }
    },
    [hasSubmittedOnce, mode, validatePassword]
  );

  const validateAndSetError = useCallback((): string | null => {
    const error = validatePassword(password);
    if (error) {
      setPasswordError(error);
    }
    return error;
  }, [password, validatePassword]);

  const markAsSubmitted = useCallback(() => {
    setHasSubmittedOnce(true);
  }, []);

  const resetPasswordState = useCallback(() => {
    setPassword("");
    setPasswordError(null);
    setHasSubmittedOnce(false);
  }, []);

  return {
    password,
    passwordError,
    hasSubmittedOnce,
    handlePasswordChange,
    validateAndSetError,
    markAsSubmitted,
    resetPasswordState,
  };
}
